/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MembersViewAvailableFilters } from '@app/app/page-modules/admin/components/members/admin-member-control-panel/admin-member-control-panel.component';
import { PlaylistMetricsTableDialogComponent } from '@app/app/page-modules/pages/components/edit/analytics/details-dialog/playlist-metrics-table-dialog.component';
import { PlaylistMetadata, RefreshActionType } from '@app/app/page-modules/pages/components/edit/analytics/page-analytics.model';
import { PageState } from '@app/app/page-modules/pages/store/pages.state';
import { RequirementsMode } from '@app/app/page-modules/playlist/models';
import { CORE_PLAYLIST_DATA_SERVICE, PlaylistDataService } from '@app/app/page-modules/playlist/services/create/core/data.service';
import { AnalyticsHelpComponent, AnalyticsHelpType } from '@app/app/shared/components/analytics/analytics-help/analytics-help.component';
import { EventsChartType } from '@app/app/shared/components/analytics/daily-enrollment-chart/daily-enrollment-chart.component';
import { developmentDomains } from '@app/app/shared/helpers/development-domains.helper';
import { Organization } from '@app/app/shared/models';
import { AndRequest, BooleanFilter, BooleanFilterType, BooleanQueryType } from '@app/app/shared/models/admin/boolean-filters.model';
import { CardEventRegistrations, IndividualPlaylistMetric, PageMemberMetrics } from '@app/app/shared/models/analytics/analytics.model';
import { Page } from '@app/app/shared/models/page/page.model';
import { LoadableState } from '@app/app/shared/store';
import { createEmptyQuery } from '@app/app/shared/utils/analytics.utils';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ptl-page-analytics',
  templateUrl: './page-analytics.component.html',
  styleUrls: ['./page-analytics.component.scss'],
  providers: [DatePipe],
})
export class PageAnalyticsComponent implements OnInit, OnDestroy {
  @Select(PageState.page)
  page$: Observable<LoadableState<Page>>;

  @Select(UserAuthState.organizationDetails)
  private organizationData$: Observable<Organization>;

  readonly courseFilterLabels = this.buildCourseFilterLabels();
  readonly eventFilterLabels = this.buildEventFilterLabels();

  filters: BooleanFilter[] = [];
  currentFilter: MembersViewAvailableFilters;
  newFilter: MembersViewAvailableFilters;
  requirementsMode: RequirementsMode = 'PART_OF';
  relevantPlaylists: PlaylistMetadata[];
  showHelp = false;
  showSkeletonView = true;
  hasRelevantPlaylists = true;
  metricsBundle: PageMemberMetrics;
  individualPlaylistMetricsWithHeaders: IndividualPlaylistMetric[] = [];
  playlistUids: string[];
  selectedEventId: string = null;
  registeredUsers: number;
  participatedUsers: number;
  waitlistedUsers: number;
  eventCardCount: number;
  startedCourseCount: number;
  completedCourseCount: number;
  membersCount: number;
  totalCardsCount: number;
  registrationDatesToCounts: Record<string, number> = {};
  eventIdsToRegistrationCounts: Record<string, number>;
  isDev = true;

  private subscriptionEnd$ = new EventEmitter<void>();
  private page: Page;
  private currentRequest: AndRequest;
  private refreshActionType: RefreshActionType = 'REFRESH_ALL';

  protected readonly EventsChartType = EventsChartType;
  protected readonly Object = Object;
  private readonly filterTypeMap: Record<RefreshActionType, BooleanFilterType[]> = {
    REFRESH_COURSES: ['GROUP', 'ROLE', 'ACTIVITY', 'REVIEW_STATUS'],
    REFRESH_EVENTS: ['ATTENDANCE'],
    REFRESH_ALL: ['GROUP', 'ROLE', 'ACTIVITY', 'REVIEW_STATUS', 'ATTENDANCE'],
  };

  get hasFewerThanThreeActiveViewers(): boolean {
    if (!this.metricsBundle?.viewMetrics?.userViewCounts) {
      return false;
    }
    const activeViewersCount = Object.entries(this.metricsBundle.viewMetrics.userViewCounts).filter(([_, views]) => views > 0).length;
    return activeViewersCount < 3;
  }

  constructor(
    @Inject(CORE_PLAYLIST_DATA_SERVICE) private playlistDataService: PlaylistDataService,
    private translocoService: TranslocoService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.showSkeletonView = true;

    this.organizationData$.pipe(take(1)).subscribe((organization) => {
      this.isDev = developmentDomains(organization?.domain);
    });

    this.page$
      .pipe(
        map((value) => value.data),
        filter((data) => !!data),
        distinctUntilChanged(),
        take(1),
        takeUntil(this.subscriptionEnd$),
      )
      .subscribe((page) => {
        this.page = page;
        this.initializePlaylists();
      });
    Chart.register(ChartDataLabels);
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$.emit();
    Chart.unregister(ChartDataLabels);
  }

  openHelpDialog() {
    this.dialog.open(AnalyticsHelpComponent, {
      width: '70vw',
      data: { analyticsHelpType: AnalyticsHelpType.PAGE_ANALYTICS },
      panelClass: 'analytics-help-dialog',
    });
  }

  filterSelected(filterType: BooleanFilterType): void {
    this.currentFilter = filterType as MembersViewAvailableFilters;
    if (!this.filters.map((f) => f.type).includes(this.currentFilter)) {
      this.newFilter = this.currentFilter;
    }
  }

  onRemoveFilter(removedFilterType: BooleanFilterType): void {
    this.currentFilter = undefined;
    this.newFilter = undefined;
    const initialFilterCount = this.filters.length;
    this.filters = this.filters.filter((f) => f.type !== removedFilterType);

    if (this.filters.length < initialFilterCount) {
      this.refreshActionType = this.getRefreshActionType(removedFilterType);
      this.applyFilters();
    }
  }

  onSaveFilter(savedFilter: BooleanFilter): void {
    this.currentFilter = undefined;
    this.newFilter = undefined;
    this.filters = this.filters.filter((currentFilter) => currentFilter.type !== savedFilter.type);
    this.filters = [...this.filters, savedFilter].sort((f1, f2) => {
      const order = ['GROUP', 'ROLE', 'ATTENDANCE', 'REVIEW_STATUS', 'ACTIVITY', 'COMPLETION'];
      const idx1 = order.findIndex((t) => f1.type === t);
      const idx2 = order.findIndex((t) => f2.type === t);
      return idx1 - idx2;
    });

    this.refreshActionType = this.getRefreshActionType(savedFilter.type);
    this.applyFilters();
  }

  openDetailsDialog(): void {
    this.dialog.open(PlaylistMetricsTableDialogComponent, {
      width: '94vw',
      data: {
        pageHeadline: this.page.headline,
        playlistData: this.individualPlaylistMetricsWithHeaders,
      },
      panelClass: 'analytics-playlists-table-dialog',
    });
  }

  onEventSelected(selectedEventId: string): void {
    this.selectedEventId = selectedEventId;
    this.updateEventMetrics();
  }

  private applyFilters(): void {
    const supportedTypes = this.filterTypeMap[this.refreshActionType] || [];
    const args = this.filters.filter((f) => supportedTypes.includes(f.type)).flatMap((f) => f.request);

    const request = {
      type: BooleanQueryType.AND,
      args: args,
    } as AndRequest;

    if (!this.metricsBundle || this.filtersHaveChanged(request)) {
      this.metricsBundle = this.initializeMetricsBundle();
    }

    this.fetchPlaylistMetrics(request);
  }

  private filtersHaveChanged(newRequest: AndRequest): boolean {
    return JSON.stringify(newRequest) !== JSON.stringify(this.currentRequest);
  }

  private initializeMetricsBundle(): PageMemberMetrics {
    return {
      summary: {
        totalCards: 0,
        totalMembers: 0,
        startedCourseCount: 0,
        completedCourseCount: 0,
        totalEnrollmentsCount: 0,
      },
      cardMemberMetrics: {
        userEngagements: {},
        cardEventRegistrations: [],
      },
      dailyEnrollmentCounts: {},
      viewMetrics: {
        totalViews: 0,
        uniqueViewers: 0,
        dailyViewCounts: {},
        userViewCounts: {},
        deviceCounts: {},
      },
      usersInfo: [],
      cardEventsInfo: [],
      individualPlaylistMetrics: [],
    };
  }

  private buildCourseFilterLabels(): { type: string; value: string }[] {
    return [
      { type: 'GROUP', value: this.translocoService.translate('translations.reviews.filter.group') },
      { type: 'ROLE', value: this.translocoService.translate('translations.filter.role.name') },
      { type: 'ACTIVITY', value: this.translocoService.translate('translations.filter.activity.nameAlt') },
    ];
  }

  private buildEventFilterLabels(): { type: string; value: string }[] {
    return [{ type: 'ATTENDANCE', value: this.translocoService.translate('translations.filter.attendance.name') }];
  }

  private initializePlaylists(): void {
    this.relevantPlaylists = this.getPlaylistsBySectionType(this.page, ['FEATURED', 'CALLOUT_FEATURED']);
    if (this.relevantPlaylists.length === 0) {
      this.hasRelevantPlaylists = false;
    }

    this.playlistUids = this.relevantPlaylists.map((playlist) => playlist.id);
    this.fetchPlaylistMetrics();
  }

  private fetchPlaylistMetrics(queryRequest?: AndRequest): void {
    const request = { query: queryRequest || createEmptyQuery() };
    this.currentRequest = queryRequest;

    this.playlistDataService
      .getPageMetrics(this.page._id, request)
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe((result) => {
        if (result.isSuccess) {
          this.metricsBundle = result.value;
          this.mapPlaylistHeaders();

          if (this.refreshActionType === 'REFRESH_COURSES') {
            this.updateCourseMetrics();
          } else if (this.refreshActionType === 'REFRESH_EVENTS') {
            this.updateEventMetrics();
          } else {
            this.updateCourseMetrics();
            this.updateEventMetrics();
          }
        }
        this.showSkeletonView = false;
      });
  }

  private mapPlaylistHeaders(): void {
    this.individualPlaylistMetricsWithHeaders = this.metricsBundle.individualPlaylistMetrics.map((metric) => {
      const playlist = this.relevantPlaylists.find((p) => p.id === metric.playlistId);
      return {
        ...metric,
        header: playlist?.header || metric.header,
      };
    });
  }

  private getPlaylistsBySectionType(page: Page, types: string[]): PlaylistMetadata[] {
    const sections = page.sections.filter((section) => types.includes(section.type));

    const uniquePlaylists = new Map<string, PlaylistMetadata>();
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item._id && !uniquePlaylists.has(item._id)) {
          uniquePlaylists.set(item._id, {
            id: item._id,
            uid: item.uid,
            header: item.header || item.headline,
          });
        }
      });
    });

    return Array.from(uniquePlaylists.values());
  }

  private updateCourseMetrics(): void {
    if (!this.metricsBundle) {
      return;
    }

    this.startedCourseCount = this.metricsBundle.summary.startedCourseCount;
    this.completedCourseCount = this.metricsBundle.summary.completedCourseCount;
    this.membersCount = this.metricsBundle.usersInfo.length;
    this.totalCardsCount = this.metricsBundle.summary.totalCards;
  }

  private updateEventMetrics(): void {
    if (!this.metricsBundle) {
      return;
    }

    const registrations = this.metricsBundle.cardMemberMetrics.cardEventRegistrations || [];

    const isValidRegistration = (reg: CardEventRegistrations) =>
      reg?.userEventSummary != null && (this.selectedEventId === null || reg.eventCardId === this.selectedEventId);

    const countByField = (field: keyof (typeof registrations)[0]['userEventSummary']) =>
      registrations.filter((reg) => isValidRegistration(reg) && reg.userEventSummary?.[field] !== null).length;

    this.eventCardCount = this.selectedEventId !== null ? 1 : this.metricsBundle.cardEventsInfo.length || 0;

    this.registeredUsers = countByField('registeredOn');
    this.participatedUsers = countByField('participatedOn');
    this.waitlistedUsers = countByField('waitlistedOn');

    this.updateDailyEnrollmentCounts();
  }

  private updateDailyEnrollmentCounts(): void {
    const registrations = this.metricsBundle?.cardMemberMetrics?.cardEventRegistrations || [];

    this.registrationDatesToCounts = registrations.reduce(
      (acc, { eventCardId, userEventSummary }) => {
        const { registeredOn } = userEventSummary || {};

        if (registeredOn && (this.selectedEventId === null || eventCardId === this.selectedEventId)) {
          const date = new Date(registeredOn).toISOString().split('T')[0];
          acc[date] = (acc[date] || 0) + 1;
        }

        return acc;
      },
      {} as Record<string, number>,
    );

    if (this.selectedEventId !== null) {
      this.eventIdsToRegistrationCounts = null;
      return;
    }

    this.eventIdsToRegistrationCounts = registrations.reduce(
      (acc, { eventCardId, userEventSummary }) => {
        const { registeredOn } = userEventSummary || {};

        if (registeredOn) {
          acc[eventCardId] = (acc[eventCardId] || 0) + 1;
        } else if (!(eventCardId in acc)) {
          acc[eventCardId] = 0;
        }

        return acc;
      },
      {} as Record<string, number>,
    );
  }

  private getRefreshActionType(filterType: BooleanFilterType): RefreshActionType {
    if (filterType === 'ATTENDANCE') {
      return 'REFRESH_EVENTS';
    } else {
      return 'REFRESH_COURSES';
    }
  }
}
